import { useEffect, useRef, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { Link } from "react-router-dom";
import type {
  LabObservation,
  LabResult,
  LabReferenceInterval,
} from "@/types/labs";
import { labReferenceRange, rangeText } from "./labReferenceRanges";
import { labDate } from "./labPresentation";
import {
  labCategory,
  labFlag,
  labFlagLabel,
  markerHistory,
  type LabRecord,
  type MarkerPoint,
} from "./labBiomarkers";

export function LabStatus({ item }: { item: LabObservation }) {
  const flag = labFlag(item);
  return (
    <span
      className={`inline-flex items-center gap-6 text-12 leading-130 ${flag === "flagged" ? "text-ink bg-warning-wash" : flag === "normal" ? "text-success bg-success-wash" : "text-ink-secondary bg-sunken"} px-8 py-5`}
    >
      <span
        aria-hidden
        className={`w-5 h-5 rounded-full shrink-0 ${flag === "flagged" ? "bg-warning" : flag === "normal" ? "bg-success" : "bg-ink-muted"}`}
      />
      {labFlagLabel(item)}
    </span>
  );
}

export function LabTrend({
  points,
  compact = false,
  range,
}: {
  points: MarkerPoint[];
  compact?: boolean;
  range?: LabReferenceInterval | null;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(compact ? 120 : 640);
  useEffect(() => {
    if (!svg.current || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0)
        setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(svg.current);
    return () => observer.disconnect();
  }, [points.length > 0]);
  const shown = points.slice(-8);
  if (!shown.length) return null;
  const values = shown.map((point) => point.value);
  const bounds = range
    ? [range.low, range.high].filter((v): v is number => v !== null)
    : [];
  const min = Math.min(
      ...values,
      ...bounds,
      ...(range?.low === null && range.high !== null ? [range.high * 0.5] : []),
    ),
    max = Math.max(
      ...values,
      ...bounds,
      ...(range?.high === null && range.low !== null ? [range.low * 1.5] : []),
    );
  const padding = Math.max((max - min) * 0.25, Math.abs(max) * 0.05, 0.1);
  const bottom = min >= 0 ? Math.max(0, min - padding) : min - padding,
    top = max + padding;
  const width = measuredWidth,
    height = compact ? 38 : 220;
  const left = compact ? 8 : 65,
    right = width - (compact ? 8 : 28);
  const yTop = compact ? 6 : 30,
    yBottom = height - (compact ? 6 : 48);
  const yFor = (value: number) =>
    yBottom - ((value - bottom) / (top - bottom)) * (yBottom - yTop);
  const bandColor =
    range?.source === "published" ? "var(--info)" : "var(--success)";
  const ticks = [...new Set([...bounds, min, max, (min + max) / 2])].filter(
    (value, index, all) =>
      !all
        .slice(0, index)
        .some((other) => Math.abs(yFor(value) - yFor(other)) < 20),
  );
  const coords = shown.map((point) => ({
    x:
      shown.length === 1
        ? (left + right) / 2
        : left +
          ((point.time - shown[0].time) /
            (shown[shown.length - 1].time - shown[0].time || 1)) *
            (right - left),
    y: yBottom - ((point.value - bottom) / (top - bottom)) * (yBottom - yTop),
  }));
  return (
    <svg
      ref={svg}
      style={{ height }}
      viewBox={`0 0 ${width} ${height}`}
      className="w-full text-accent overflow-visible"
      role={compact ? undefined : "img"}
      aria-hidden={compact || undefined}
      aria-label={
        compact
          ? undefined
          : `Recorded values over time. ${shown.map((point) => `${labDate(point.date)}: ${point.item.value_text}`).join("; ")}${range ? `. ${range.label}: ${rangeText(range)}` : ""}`
      }
    >
      {range && (
        <g aria-hidden="true">
          <rect
            x={left}
            y={range.high === null ? yTop : yFor(range.high)}
            width={Math.max(0, right - left)}
            height={Math.max(
              0,
              (range.low === null ? yBottom : yFor(range.low)) -
                (range.high === null ? yTop : yFor(range.high)),
            )}
            fill={
              range.source === "published"
                ? "var(--info-wash)"
                : "var(--success-wash)"
            }
          />
          {bounds.map((bound) => (
            <line
              key={bound}
              x1={left}
              x2={right}
              y1={yFor(bound)}
              y2={yFor(bound)}
              stroke={bandColor}
              strokeDasharray="4 4"
            />
          ))}
        </g>
      )}
      {!compact &&
        ticks.map((value) => {
          const y =
            yBottom - ((value - bottom) / (top - bottom)) * (yBottom - yTop);
          return (
            <g key={value}>
              <line
                x1={left}
                x2={right}
                y1={y}
                y2={y}
                stroke={
                  bounds.includes(value) ? bandColor : "var(--line-subtle)"
                }
                strokeDasharray={bounds.includes(value) ? "4 4" : undefined}
              />
              <text
                x={left - 12}
                y={y + 4}
                textAnchor="end"
                fill="var(--ink-secondary)"
                fontSize="12"
              >
                {Number(value.toFixed(2))}
              </text>
            </g>
          );
        })}
      {shown.length > 1 && (
        <polyline
          points={coords.map((point) => `${point.x},${point.y}`).join(" ")}
          fill="none"
          stroke="currentColor"
          strokeWidth={compact ? 1.5 : 2}
        />
      )}
      {coords.map((point, index) => (
        <g key={index}>
          <circle
            cx={point.x}
            cy={point.y}
            r={compact ? 3 : 5}
            fill="currentColor"
            stroke="var(--surface-page)"
            strokeWidth="2"
          />
          {!compact && (index === 0 || index === shown.length - 1) && (
            <text
              x={point.x}
              y={height - 15}
              textAnchor={
                shown.length === 1 ? "middle" : index === 0 ? "start" : "end"
              }
              fill="var(--ink-secondary)"
              fontSize="12"
            >
              {labDate(shown[index].date)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export function LabBiomarkerDetail({
  item,
  result,
  records,
  onClose,
  onReport,
  clinical = false,
  download,
}: {
  item: LabObservation | null;
  result: LabResult;
  records: LabRecord[];
  onClose: () => void;
  onReport: () => void;
  clinical?: boolean;
  download?: ReactNode;
}) {
  const points = item ? markerHistory(item, result, records) : [];
  const range = item ? labReferenceRange(item) : null;
  return (
    <Dialog.Root
      open={!!item}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-ink/35 backdrop-blur-[4px]" />
        <Dialog.Content className="ph-no-capture fixed z-[111] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(76rem,calc(100vw-24px))] max-h-[90dvh] flex flex-col bg-page border border-line-subtle rounded-card overflow-hidden">
          {item && (
            <>
              <header className="flex justify-between gap-15 p-20 sm:p-25 border-b border-line-subtle">
                <div className="min-w-0">
                  <p className="type-label-xs text-ink-secondary mb-8">
                    {labCategory(item).name}
                  </p>
                  <Dialog.Title className="font-display text-24 sm:text-28 leading-120">
                    {item.name}
                  </Dialog.Title>
                  <Dialog.Description className="type-body-sm text-ink-secondary mt-8">
                    Collected{" "}
                    {labDate(item.collected_at || result.collected_at)}
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <Button
                    variant="icon"
                    aria-label="Close biomarker details"
                    className="shrink-0"
                  >
                    <X size={20} />
                  </Button>
                </Dialog.Close>
              </header>
              <div className="p-20 sm:p-25 overflow-y-auto min-h-0">
                <div className="flex items-start justify-between flex-wrap gap-15">
                  <div>
                    <p className="type-label-xs text-ink-secondary">
                      Selected result
                    </p>
                    <p className="font-display text-36 sm:text-40 leading-110 mt-10 break-words tabular-nums">
                      {item.value_text || "Not reported"}
                    </p>
                    {range && (
                      <p className="type-body-sm text-ink-secondary leading-145 mt-10">
                        {range.label}: {rangeText(range)}
                      </p>
                    )}
                  </div>
                  <LabStatus item={item} />
                </div>
                <section
                  className="mt-25 border border-line-subtle bg-raised p-15 sm:p-20"
                  aria-label="Result history"
                >
                  <div className="flex justify-between gap-10">
                    <h3 className="type-label-sm">Result history</h3>
                    <span className="type-body-sm text-ink-secondary">
                      {points.length} recorded{" "}
                      {points.length === 1 ? "value" : "values"}
                    </span>
                  </div>
                  {points.length > 0 && (
                    <div className="mt-15">
                      <LabTrend points={points} range={range} />
                    </div>
                  )}
                  <p className="type-body-sm text-ink-secondary leading-145 mt-10">
                    {points.length > 1
                      ? "Recorded values with matching units. Earlier measurements are listed below."
                      : points.length === 1
                        ? "Your first recorded value. A trend will appear after another comparable test."
                        : "A chart is not available for this measurement. Your source value is shown above."}
                  </p>
                  {range && (
                    <p className="type-body-sm text-ink-secondary leading-145 mt-10">
                      <span
                        className={`inline-block w-10 h-10 mr-6 ${range.source === "published" ? "bg-info" : "bg-success"}`}
                        aria-hidden
                      />
                      {range.label}:{" "}
                      <strong className="text-ink font-medium">
                        {rangeText(range)}
                      </strong>
                      .
                      {points.length > 1 &&
                        " Band applies to the selected sample; earlier reference ranges may differ."}
                    </p>
                  )}
                  {points.length > 8 && (
                    <p className="type-body-sm text-ink-secondary mt-8">
                      Chart shows the eight most recent collection dates.
                    </p>
                  )}
                </section>
                <section
                  className="mt-25"
                  aria-label={range?.label || "Laboratory reference range"}
                >
                  <h3 className="type-label-sm">
                    {range?.label || "Laboratory reference range"}
                  </h3>
                  {range && (
                    <div className="mt-10 border border-line-subtle p-15 bg-raised">
                      <p className="font-display text-24 tabular-nums">
                        {rangeText(range)}
                      </p>
                      {range.population && (
                        <p className="type-body-sm text-ink-secondary mt-8">
                          {range.population}
                        </p>
                      )}
                      <p className="type-body-sm text-ink-secondary leading-145 mt-8">
                        {range.source === "laboratory"
                          ? range.origin === "source_report"
                            ? "From the cutoff/expected values printed in your original laboratory report."
                            : "Supplied by the reporting laboratory for this sample."
                          : range.note}
                      </p>
                      {range.source_url && (
                        <a
                          className="inline-block mt-10 type-body-sm underline underline-offset-4"
                          href={range.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Source: {range.source_title} ↗
                        </a>
                      )}
                    </div>
                  )}
                  {item.reference_ranges?.length ? (
                    item.reference_ranges.map((range, index) => (
                      <p
                        key={index}
                        className="type-body-md leading-150 mt-10 whitespace-pre-wrap"
                      >
                        {range}
                      </p>
                    ))
                  ) : !range ? (
                    <p className="type-body-md text-ink-secondary leading-145 mt-10">
                      No structured reference range was supplied. Check the
                      original report for the laboratory’s range and full
                      context.
                    </p>
                  ) : null}
                </section>
                {item.interpretation.length > 0 && (
                  <p className="type-body-sm text-ink-secondary mt-15">
                    Laboratory flag: {item.interpretation.join(" · ")}
                  </p>
                )}
                {(item.notes.length > 0 || item.specimen_id) && (
                  <section className="mt-25 border-t border-line-subtle pt-20">
                    <h3 className="type-label-sm">
                      Laboratory notes + details
                    </h3>
                    {item.notes.map((note, i) => (
                      <p
                        key={i}
                        className="type-body-md text-ink-secondary leading-150 whitespace-pre-wrap mt-10"
                      >
                        {note}
                      </p>
                    ))}
                    {item.specimen_id && (
                      <p className="type-body-sm text-ink-secondary mt-10 break-all">
                        Specimen {item.specimen_id}
                      </p>
                    )}
                  </section>
                )}
                {points.length > 1 && (
                  <div className="mt-25 border-t border-line-subtle">
                    <h3 className="type-label-sm py-15">
                      Previous measurements
                    </h3>
                    {[...points].reverse().map((point) => (
                      <div
                        key={point.date}
                        className="flex flex-wrap justify-between gap-10 py-12 border-t border-line-subtle type-body-sm"
                      >
                        <span>{labDate(point.date)}</span>
                        <span className="font-display tabular-nums">
                          {point.item.value_text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {!clinical && (
                  <div className="mt-25 border-t border-line-subtle pt-20">
                    <h3 className="font-display text-20">
                      Questions about this result?
                    </h3>
                    <p className="type-body-md text-ink-secondary mt-8 leading-145">
                      Your care team can help you understand this measurement in
                      the context of your health.
                    </p>
                    <Button variant="secondary" className="mt-15" asChild>
                      <Link to="/app/clinic" onClick={onClose}>
                        Contact care team <ArrowRight size={14} />
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
              <footer className="p-15 sm:px-25 border-t border-line-subtle bg-raised flex flex-wrap gap-10 justify-end">
                {download}
                <Button
                  variant="secondary"
                  disabled={!result.report_available}
                  onClick={() => {
                    onClose();
                    onReport();
                  }}
                >
                  View original report <ArrowRight size={14} />
                </Button>
              </footer>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
